import { EMPTY, fromEvent, identity, merge, Observable, of } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  pairwise,
  share,
  startWith,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { shallowEqualObjects } from 'shallow-equal';
import { isClipboardEventWithData, isDragEventWithDataTransfer } from '../../helpers/dataTransfer';
import { DECORATION_ATTRIBUTE_NAME, getDecorationNodes } from '../../helpers/decoration';
import {
  isBlockLevelContent,
  isPhrasingContent,
  isPresentationalPhrasingContent,
} from '../../helpers/dom/content';
import { unwrapElement } from '../../helpers/dom/element';
import { getGlobalWindow } from '../../helpers/dom/global';
import {
  findAncestor,
  getTextNodes,
  isComment,
  isContentEditableInEditor,
  isElement,
  isElementWithTagName,
  isText,
  isWhitespaceText,
} from '../../helpers/dom/node';
import { paintNodesInRange } from '../../helpers/dom/paint';
import { createPointWithOffsetInContainer } from '../../helpers/dom/point';
import { createRangeWithTextRange, getTextContentInRange } from '../../helpers/dom/range';
import { isKarmaTestEnv } from '../../helpers/env';
import { MutationFrame } from '../../helpers/fromMutations';
import { createInsertionPoint, INSERTION_POINT_FILLER } from '../../helpers/insertionPoint';
import {
  concatIterables,
  everyInIterable,
  filterIterable,
  flatMapIterable,
  isEmptyIterable,
  mapIterable,
} from '../../helpers/iterable';
import {
  getTextMutationAffinityFromMutationRecord,
  isMutationAffinityEqual,
  MutationAffinity,
  OTHER_MUTATION_AFFINITY,
} from '../../helpers/mutationAffinity';
import { SanitizeHtmlOptions } from '../../helpers/sanitizeHtml';
import { isSelectionBookmarkAttributeName } from '../../helpers/selectionBookmarks';
import { InteractionHandler } from '../../interactionEvent';
import { createConnectingService } from '../../services/base/connectingService';
import { createContentEditableTabKeyService } from '../../services/dom/contentEditableTabKeyService';
import { createElementEmptyService } from '../../services/dom/elementEmptyService';
import { createElementFocusService } from '../../services/dom/elementFocusService';
import {
  createElementSelectionService,
  ElementSelectionService,
} from '../../services/dom/elementSelectionService';
import { createElementEffectService } from '../../services/dom/elementService';
import { createElementTextDirectionService } from '../../services/dom/elementTextDirectionService';
import { createElementTextService } from '../../services/dom/elementTextService';
import { createElementUndoService } from '../../services/dom/elementUndoService';
import { createElementUppercaseService } from '../../services/dom/elementUppercaseService';
import { FocusService } from '../../services/dom/focusService';
import { createEmbedService, EmbedService } from '../../services/embed/embedService';
import { createElementHtmlEditService } from '../../services/html/elementHtmlEditService';
import { createElementHtmlService } from '../../services/html/elementHtmlService';
import { HtmlEditService } from '../../services/html/htmlEditService';
import { createHtmlEmptyService } from '../../services/html/htmlEmptyService';
import { HtmlService } from '../../services/html/htmlService';
import { buttonPanelClassnames, decoratedCellsClassnames } from '../../services/html/table/decorateTable';
import { CommitService, createSimpleCommitService } from '../../services/text/commitService';
import { EmptyService } from '../../services/text/emptyService';
import { TextService } from '../../services/text/textService';
import { UndoService } from '../../services/text/undoService';
import { ConnectingEditor, createConnectingEditor } from './connectingEditor';
import { EditorServices, EditorSpec } from './editor';
import {
  ContentEditableEditorDecoration,
  ContentEditableEditorPlugin,
} from './plugins/contentEditableEditorPlugin';
import { ContentEditableEditorPluginCollection } from './plugins/contentEditableEditorPluginCollection';
import { createInsertionPointPlugin } from './plugins/insertionPointPlugin';

export type ContentEditableElement = HTMLDivElement;

export interface ContentEditableEditor<S extends EditorServices = ContentEditableEditorServices>
  extends ConnectingEditor<ContentEditableElement, S> {}

export type EditorTrace = { type: 'copy' | 'cut' | 'paste'; duration: number; dataTransfer: DataTransfer };

export type ContentEditableEditorServices = {
  html: HtmlService;
  text: TextService;
  empty: EmptyService;
  edit: HtmlEditService;
  commit: CommitService;
  focus: FocusService;
  selection: ElementSelectionService;
  undo: UndoService;
  embed: EmbedService;
};

export type ContentEditableEditorOptions = {
  sanitizeHtmlOptions?: SanitizeHtmlOptions;
  simplifyStylesMaxHtmlLength?: number;
  didSetHtml?: (element: HTMLElement, htmlLength: number) => void;
  plugins?: ReadonlyArray<ContentEditableEditorPlugin>;

  /**
   * Whether to use the native delete/backspace handling.
   *
   * The default is a custom implementation to workaround this bug:
   * https://bugs.chromium.org/p/chromium/issues/detail?id=582844#c31
   */
  shouldNativeDelete?: boolean;

  onInteraction?: InteractionHandler;
  onTrace?: (trace: EditorTrace) => void;

  hasTableService?: boolean;
  hasNewDeleteSelection?: boolean;
  shouldKeepPreformattedText?: boolean;
  shouldPasteAsTextInCodeBlocks?: boolean;
  hasTextDirectionService?: boolean;
};

type ContentEditableEditorSpecCreator<S extends EditorServices = ContentEditableEditorServices> = (
  baseSpec: EditorSpec<ContentEditableEditorServices>,
  element$: Observable<ContentEditableElement | null>,
  options?: ContentEditableEditorOptions,
) => EditorSpec<S>;

export function createContentEditableEditor<S extends EditorServices>(
  options: ContentEditableEditorOptions,
  createSpec: ContentEditableEditorSpecCreator<S>,
): ContentEditableEditor<S>;
export function createContentEditableEditor(
  options?: ContentEditableEditorOptions,
): ContentEditableEditor<ContentEditableEditorServices>;
export function createContentEditableEditor<S extends EditorServices = ContentEditableEditorServices>(
  options: ContentEditableEditorOptions = {},
  createSpec: ContentEditableEditorSpecCreator<S> = identity as ContentEditableEditorSpecCreator<S>,
): ContentEditableEditor<S> {
  return createConnectingEditor(element$ => {
    const baseSpec = createContentEditableEditorSpec(element$, options);
    const spec = createSpec(baseSpec, element$, options);
    return spec;
  });
}

export function createContentEditableEditorSpec(
  element$: Observable<ContentEditableElement | null>,
  options: ContentEditableEditorOptions = {},
): EditorSpec<ContentEditableEditorServices> {
  const {
    sanitizeHtmlOptions,
    simplifyStylesMaxHtmlLength,
    didSetHtml,
    onTrace,
    hasTableService,
    hasNewDeleteSelection,
    shouldKeepPreformattedText,
    shouldPasteAsTextInCodeBlocks,
    hasTextDirectionService,
  } = options;

  const customPlugins = options.plugins || [];
  const plugins = new ContentEditableEditorPluginCollection([
    createCoreBeforePlugin(),
    createInsertionPointPlugin(),
    ...customPlugins,
  ]);
  let prevMutationAffinity: MutationAffinity | undefined;

  const htmlService = createElementHtmlService(element$, {
    sanitizeHtmlOptions,
    processElement: element => processElement(element, embedService, plugins),
    performCascadingMutations: (element, frame) => {
      const nextMutationAffinity = performCascadingMutations(
        element,
        selectionService,
        embedService,
        plugins,
        frame,
      );

      // If the mutation affinity was the same thing we did before, skip adding an undo checkpoint.
      if (prevMutationAffinity && !isMutationAffinityEqual(prevMutationAffinity, nextMutationAffinity)) {
        undoService.commitLastSnapshot();
      }

      prevMutationAffinity = nextMutationAffinity;
    },
    filterMutationRecord,
    didSetHtml,
  });

  const commitService = createSimpleCommitService(htmlService.waitForChanges);
  const focusService = createElementFocusService(element$);

  const emptyService = createConnectingService(
    element$,
    () => createHtmlEmptyService(htmlService),
    element => createElementEmptyService(element),
  );

  const selectionService = createElementSelectionService(element$, focusService, htmlService);

  const textService = createElementTextService(element$, htmlService, selectionService, {
    decorateText: element => triggerTextDecoration(element, plugins),
  });

  const contentEditableConfigService = createElementEffectService(element$, (element, document) =>
    merge(
      // Configure each contentEditable every time it receives focus.
      fromEvent(element, 'focus').pipe(
        startWith(undefined),
        tap(() => {
          document.execCommand('defaultParagraphSeparator', false, 'div');

          // Because spans with inline styles are easier to remove than font elements.
          // @ts-ignore
          document.execCommand('styleWithCSS', false, true);
        }),
        share(),
      ),
    ),
  );

  // Take undo snapshots after the DOM is modified and/or the selection changes.
  const undoService = createElementUndoService(
    element$,
    {
      elementToRemoveFromSnapshotSelectors: new Set([`.${buttonPanelClassnames.PANEL}`]),
      classToRemoveFromSnapshotSelectors: new Set(Object.values(decoratedCellsClassnames)),
    },
    htmlService,
    selectionService,
  );

  const editService = createElementHtmlEditService(element$, selectionService, {
    sanitizeHtmlOptions,
    simplifyStylesMaxHtmlLength,
    preProcessInsertedHtml: (dirtyHtml, document) => plugins.preProcessInsertedHtml(dirtyHtml, document),
    postProcessInsertedHtml: cleanHtml => plugins.postProcessInsertedHtml(cleanHtml),
    hasTableService,
    hasNewDeleteSelection,
    shouldKeepPreformattedText,
    shouldPasteAsTextInCodeBlocks,
  });

  const embedService = createEmbedService(element$, editService, selectionService);

  // Handle delete and backspace.
  // Works around a Chrome performance bug: https://bugs.chromium.org/p/chromium/issues/detail?id=582844#c31
  const deleteService = createElementEffectService(element$, element =>
    options.shouldNativeDelete
      ? EMPTY
      : fromEvent<KeyboardEvent>(element, 'keydown').pipe(
          tap(event => {
            if (event.defaultPrevented) {
              return;
            }

            if (event.key !== 'Backspace' && event.key !== 'Delete') {
              return;
            }

            const { selection } = selectionService;
            if (!selection || selection.range.collapsed) {
              return;
            }

            editService.deleteSelection();
            event.preventDefault();
          }),
          share(),
        ),
  );

  // Handle pastes and drops.
  const pasteService = createElementEffectService(element$, element =>
    merge(
      fromEvent<ClipboardEvent>(element, 'paste').pipe(
        filter(isClipboardEventWithData),
        map(event => ({ event, dataTransfer: event.clipboardData })),
      ),
      fromEvent<DragEvent>(element, 'drop').pipe(
        filter(isDragEventWithDataTransfer),
        map(event => ({ event, dataTransfer: event.dataTransfer })),
      ),
    ).pipe(
      tap(({ event, dataTransfer }) => {
        if (event.defaultPrevented) {
          return;
        }

        const window = getGlobalWindow();
        const before = window && onTrace ? window.performance.now() : 0;

        if (!plugins.handleDataTransfer(dataTransfer, editService, element)) {
          return;
        }

        event.stopPropagation();
        event.preventDefault();

        const after = window && onTrace ? window.performance.now() : 0;
        if (onTrace) {
          onTrace({ type: 'paste', duration: after - before, dataTransfer });
        }
      }),
      share(),
    ),
  );

  const copyService = createElementEffectService(element$, element =>
    merge(fromEvent<ClipboardEvent>(element, 'cut'), fromEvent<ClipboardEvent>(element, 'copy')).pipe(
      filter(isClipboardEventWithData),
      tap(event => {
        const eventType = event.type as 'cut' | 'copy';
        const window = getGlobalWindow();
        const before = window && onTrace ? window.performance.now() : 0;

        if (!plugins.populateDataTransfer(event.clipboardData, element)) {
          return;
        }

        event.stopPropagation();
        event.preventDefault();

        if (eventType === 'cut') {
          editService.deleteSelection();
        }

        const after = window && onTrace ? window.performance.now() : 0;
        if (onTrace) {
          onTrace({ type: eventType, duration: after - before, dataTransfer: event.clipboardData });
        }
      }),
      share(),
    ),
  );

  const uppercaseService = createElementUppercaseService(element$, selectionService, {
    onInteraction: options.onInteraction,
  });

  const tabService = createContentEditableTabKeyService(element$, selectionService);

  // Prevent key events from bubbling during compositions, to isolate them from downstream handlers.
  const compositionIsolationService = createElementEffectService(element$, element => {
    const isComposing$ = merge(
      of(false),
      fromEvent(element, 'compositionstart').pipe(mapTo(true)),
      merge(fromEvent(element, 'compositionend'), fromEvent(element, 'blur')).pipe(mapTo(false)),
    );

    return merge(
      fromEvent(element, 'keydown'),
      fromEvent(element, 'keypress'),
      fromEvent(element, 'keyup'),
    ).pipe(
      withLatestFrom(isComposing$),
      tap(([event, isComposing]) => {
        if (isComposing) {
          event.stopPropagation();
        }
      }),
      share(),
    );
  });

  const processTextOnCursorLeaveService = createElementEffectService(element$, element =>
    merge(
      // When a commit is requested, pretend the cursor left the text node.
      commitService.changes$.pipe(
        filter(() => commitService.isCommitting),
        mapTo(null),
      ),
      // When the selection changes...
      selectionService.changes$.pipe(
        map(() => {
          // Is there a selection and does the editor have focus?
          const { isActive, selection } = selectionService;
          const isActiveOrUnfocusable = isActive || isKarmaTestEnv;
          if (!isActiveOrUnfocusable || !selection) {
            return null;
          }

          const { anchorNode, focusNode } = selection;
          // Check the user is not highlighting some text
          if (anchorNode !== focusNode) {
            return null;
          }

          return focusNode;
        }),
      ),
    ).pipe(
      // Emit only when the node changes.
      distinctUntilChanged(),
      // Emit both the previous and next node.
      pairwise(),
      tap(([prevTextNode]) => {
        // Was the cursor previously in a text node?
        if (!isText(prevTextNode)) {
          return;
        }

        // Re-process the text now that the cursor has left the text node.
        selectionService.withMutations(() => {
          // no cursorPositon to indicate the cursor is not on this text node.
          plugins.processText(element, [{ node: prevTextNode }]);
        });
      }),
      share(),
    ),
  );

  // Default to off for the text direction service for now.
  const textDirectionService = createElementTextDirectionService(
    element$,
    selectionService,
    hasTextDirectionService ?? false,
  );

  return {
    services: {
      html: htmlService,
      text: textService,
      empty: emptyService,
      edit: editService,
      commit: commitService,
      focus: focusService,
      selection: selectionService,
      undo: undoService,
      embed: embedService,
    },
    privateServices: [
      contentEditableConfigService,
      deleteService,
      pasteService,
      copyService,
      uppercaseService,
      tabService,
      compositionIsolationService,
      processTextOnCursorLeaveService,
      textDirectionService,
    ],
  };
}

function processElement(
  element: Element,
  embedService: EmbedService,
  plugins: ContentEditableEditorPluginCollection,
) {
  processEmbeds(element, embedService, plugins);
  const textNodes = getTextNodes(element);
  plugins.processText(
    element,
    mapIterable(textNodes, node => ({
      node,
    })),
  );
  decorateText(element, plugins, textNodes);
  plugins.processTree(element);
}

function performCascadingMutations(
  element: Element,
  selectionService: ElementSelectionService,
  embedService: EmbedService,
  plugins: ContentEditableEditorPluginCollection,
  { records }: MutationFrame,
): MutationAffinity {
  // The unique record types in this change set.
  const recordTypes = new Set<MutationRecordType>();
  // The first characterData record with Text target.
  let firstTextRecord: MutationRecord | undefined;
  // The unique Text nodes that have characterData records.
  const mutatedTextNodes = new Set<Text>();
  // The unique parent nodes that have childList records.
  const mutatedParentNodes = new Set<Node>();
  // The unique newly added nodes.
  const addedNodes = new Set<Node>();
  // The unique tag names of nodes that were either added or removed.
  const movedElementTagNames = new Set<string>();

  records.forEach(record => {
    recordTypes.add(record.type);

    // We don't care about nodes that have since been removed.
    if (!record.target.parentNode) {
      return;
    }

    switch (record.type) {
      case 'characterData':
        if (isText(record.target)) {
          if (!firstTextRecord) {
            firstTextRecord = record;
          }

          mutatedTextNodes.add(record.target);
        }
        break;

      case 'childList': {
        for (const node of record.addedNodes) {
          // Skip nodes that have since been removed.
          if (!node.parentNode) {
            continue;
          }

          addedNodes.add(node);
        }

        for (const node of concatIterables(record.addedNodes, record.removedNodes)) {
          if (node.parentNode && isElement(node)) {
            movedElementTagNames.add(node.tagName);
          }
        }

        mutatedParentNodes.add(record.target);
        break;
      }
      default:
    }
  });

  // Also collect all Text nodes that are descendants of newly added nodes.
  const addedNodesWithoutMutatedTextNodes = filterIterable(
    addedNodes,
    addedNode => !(mutatedTextNodes as Set<Node>).has(addedNode),
  );
  const addedTextNodes = flatMapIterable(addedNodesWithoutMutatedTextNodes, getTextNodes);
  const allTextNodes = concatIterables(mutatedTextNodes, addedTextNodes);

  const shouldProcessText = !isEmptyIterable(allTextNodes);
  const shouldProcessTree = recordTypes.has('childList') || recordTypes.has('attributes');
  if (shouldProcessText || shouldProcessTree) {
    selectionService.withMutations(() => {
      if (shouldProcessTree) {
        processEmbeds(element, embedService, plugins);
      }

      if (shouldProcessText) {
        const { selection } = selectionService;
        const textNodeAndCursorPositions = mapIterable(allTextNodes, textNode => {
          const textUnderCursor = selection && selection.anchorNode === textNode;
          return {
            node: textNode,
            cursorPosition: selection && textUnderCursor ? selection.anchorOffset : undefined,
          };
        });
        plugins.processText(element, textNodeAndCursorPositions);
        decorateText(element, plugins, allTextNodes);
      }

      if (shouldProcessTree) {
        plugins.processTree(element);
      }
    });

    selectionService.withMutations(() => {
      // Make sure the DOM stays consistent with what it would be if we get/set innerHTML.
      for (const node of mutatedParentNodes) {
        node.normalize();
      }
    });
    const currentSelection = selectionService.selection;
    if (currentSelection) {
      const { isCollapsed, anchorNode } = currentSelection;
      if (
        isCollapsed === true &&
        isText(anchorNode) &&
        anchorNode.textContent === '' &&
        isElement(anchorNode.previousSibling) &&
        anchorNode.parentNode
      ) {
        const insertionPoint = createInsertionPoint(element.ownerDocument);
        insertionPoint.textContent = INSERTION_POINT_FILLER;
        anchorNode.parentNode.replaceChild(insertionPoint, anchorNode);
        const selectPoint = createPointWithOffsetInContainer(insertionPoint, 1);
        selectionService.moveToPoint(selectPoint);
      }
    }

    selectionService.normalize();
  }

  let nextMutationAffinity: MutationAffinity = OTHER_MUTATION_AFFINITY;

  // Detect simple text insertion/removal.
  if (firstTextRecord && mutatedTextNodes.size === 1) {
    const areMovedElementsOnlyFiller =
      movedElementTagNames.size === 0 || (movedElementTagNames.size === 1 && movedElementTagNames.has('BR'));
    if (areMovedElementsOnlyFiller) {
      nextMutationAffinity = getTextMutationAffinityFromMutationRecord(firstTextRecord);
    }
  }

  return nextMutationAffinity;
}

function decorateText(
  element: Element,
  plugins: ContentEditableEditorPluginCollection,
  textNodes: Iterable<Text>,
) {
  if (!plugins.hasDecorateText) {
    return;
  }

  // TODO: Fix overlapping runs of phrasing content when non-phrasing content siblings are present.
  const phrasingContentContainers = new Set(
    flatMapIterable(textNodes, textNode => {
      const phrasingContentContainer = findAncestor(textNode, node => !isPhrasingContent(node), element);
      return phrasingContentContainer ? [phrasingContentContainer] : [];
    }),
  );

  phrasingContentContainers.forEach(phrasingContentContainer =>
    decorateTextContentInContainer(phrasingContentContainer, plugins),
  );
}

function getSortedDecorationsForText(text: string, plugins: ContentEditableEditorPluginCollection) {
  // Sort and remove overlapping decorations.
  let lastIndex = 0;
  return Array.from(plugins.decorateText(text))
    .sort((a, b) => a.index - b.index)
    .filter(decoration => {
      if (decoration.index < lastIndex) {
        return false;
      }

      lastIndex = decoration.index + decoration.length;
      return true;
    });
}

function reconcileDecorationsInContainer(
  container: Node,
  nextDecorations: ReadonlyArray<ContentEditableEditorDecoration>,
): ReadonlyArray<ContentEditableEditorDecoration> {
  const newDecorations = Array.from(nextDecorations);

  let newDecorationIndex = 0;
  for (const result of getDecorationNodes(container)) {
    if (!isElement(result.node)) {
      continue;
    }

    const prevDecoration = {
      index: result.index,
      length: result.length,
      className: result.node.className,
    };

    let didFindMatch = false;
    for (let i = newDecorationIndex; i < newDecorations.length; i++) {
      const newDecoration = newDecorations[i];

      // Compare only some properties.
      const nextDecoration: typeof prevDecoration = {
        index: newDecoration.index,
        length: newDecoration.length,
        className: newDecoration.className,
      };
      if (!shallowEqualObjects(prevDecoration, nextDecoration)) {
        continue;
      }

      newDecorations.splice(i, 1);
      newDecorationIndex = i;
      didFindMatch = true;
      break;
    }

    if (!didFindMatch) {
      unwrapElement(result.node);
    }
  }

  return newDecorations;
}

function decorateTextContentInContainer(container: Node, plugins: ContentEditableEditorPluginCollection) {
  const selection = container.ownerDocument && container.ownerDocument.getSelection();
  const { focusNode, focusOffset } = selection || { focusNode: null, focusOffset: 0 };

  const text = container.textContent || '';
  const nextDecorations = getSortedDecorationsForText(text, plugins);
  const newDecorations = reconcileDecorationsInContainer(container, nextDecorations);

  newDecorations.forEach(decoration => {
    const startIndex = decoration.index;
    const endIndex = startIndex + decoration.length;

    const range = createRangeWithTextRange(container, { startIndex, endIndex });
    if (!range) {
      return;
    }

    const expectedText = text.substring(startIndex, endIndex);
    const actualText = getTextContentInRange(range);
    if (actualText !== expectedText) {
      // eslint-disable-next-line no-console
      console.warn(`Wanted to decorate ${expectedText}, but got ${actualText}`);
      return;
    }

    // If a text node being decorated contains the cursor and the cursor is strictly after the decoration, insert a new
    // text node to decorate so that the cursor can stay on the same text node. If the cursor is exactly at the end of
    // the decoration, we decorate the original text node so the cursor will end up inside the decoration.
    if (isText(range.endContainer) && range.endContainer === focusNode && range.endOffset < focusOffset) {
      const { startContainer, startOffset } = range;
      const textNode = range.endContainer;
      if (textNode.parentNode) {
        // Inserting start text as a new text node before the existing text node.
        const beforeTextNode = textNode.cloneNode(false) as typeof textNode;
        beforeTextNode.data = textNode.data.substring(0, range.endOffset);
        textNode.data = textNode.data.substring(range.endOffset);
        textNode.parentNode.insertBefore(beforeTextNode, textNode);

        // Reset the range for the decoration, taking into account that the startContainer may be the same text node.
        range.setStart(startContainer === textNode ? beforeTextNode : startContainer, startOffset);
        range.setEndAfter(beforeTextNode);
      }
    }

    // TODO: Remove formatting to ensure it is one consecutive text node, then use spliceTextNode to insert.
    paintNodesInRange(container, range, {
      shouldPaintNode: node =>
        isPhrasingContent(node) &&
        !findAncestor(
          node,
          ancestorNode =>
            isElementWithTagName(ancestorNode, 'span') && ancestorNode.className === decoration.className,
          container,
        ),
      shouldSplitElement: splitElement => isPresentationalPhrasingContent(splitElement),
      createPaintedElement: document => {
        const decorationElement = document.createElement('span');
        decorationElement.className = decoration.className;
        decorationElement.setAttribute(DECORATION_ATTRIBUTE_NAME, '');
        if (decoration.shouldDisableSpellcheck) {
          decorationElement.setAttribute('spellcheck', 'false');
        }
        return decorationElement;
      },
    });
  });
}

function filterMutationRecord(element: Element, record: MutationRecord) {
  // Ignore changes that aren't in a content editable.
  if (!isContentEditableInEditor(record.target, element)) {
    return false;
  }

  switch (record.type) {
    case 'attributes': {
      const target = record.target as Element;
      const attributeName = record.attributeName as string;

      // Ignore attribute changes on Grammarly elements.
      if (target.tagName === 'G') {
        return false;
      }

      // Ignore attribute changes related to bookmarks.
      if (isSelectionBookmarkAttributeName(attributeName)) {
        return false;
      }

      break;
    }
    default:
  }

  return true;
}

function processEmbeds(
  element: Element,
  embedService: EmbedService,
  plugins: ContentEditableEditorPluginCollection,
) {
  const embedDefinitions = plugins.processEmbeds(element);
  embedService.processEmbeds(embedDefinitions);
}

const createCoreBeforePlugin = (): ContentEditableEditorPlugin => ({
  processTree: element => {
    const document = element.ownerDocument;
    if (!document) {
      return;
    }

    // Insert a filler if the element is (as good as) empty.
    if (everyInIterable(element.childNodes, node => isWhitespaceText(node) || isComment(node))) {
      // This will be wrapped in a paragraph in the next step.
      const br = document.createElement('br');
      element.appendChild(br);
    }

    // Wrap top-level phrasing content in paragraphs.
    let currentParagraph: Element | null = null;
    Array.from(element.childNodes).forEach(childNode => {
      if (isBlockLevelContent(childNode)) {
        if (currentParagraph) {
          currentParagraph = null;
        }

        return;
      }

      if (!currentParagraph) {
        if (isWhitespaceText(childNode) || isComment(childNode)) {
          return;
        }

        currentParagraph = document.createElement('div');
        element.insertBefore(currentParagraph, childNode);
      }

      currentParagraph.appendChild(childNode);
    });
  },
});

function triggerTextDecoration(element: Element, plugins: ContentEditableEditorPluginCollection) {
  const textNodes = getTextNodes(element);
  decorateText(element, plugins, textNodes);
}
